import React from "react";

const BASE = process.env.REACT_APP_URL || "http://127.0.0.1:8000";
const API_KEY = process.env.REACT_APP_API_KEY;
const apiHeaders = () => ({
  "Content-Type": "application/json",
  ...(API_KEY ? { "x-api-key": API_KEY } : {}),
  "ngrok-skip-browser-warning": "true",
});

// ─── Dark theme tokens ──────────────────────────────────────────────────────
export const C = {
  bg:      "#0a0c10",
  panel:   "#0f1318",
  panel2:  "#141922",
  border:  "#1e242e",
  ink:     "#f3f4f6",
  body:    "#c4cad3",
  muted:   "#8b919b",
  faint:   "#5b616b",
  grid:    "#1b2129",
  orange:  "#f98300",
  orange2: "#ffb24d",
  green:   "#22c55e",
  red:     "#ef4444",
  blue:    "#5b8def",
  navy:    "#3b5b8c",
};
export const ORANGE_GRAD = "linear-gradient(90deg,#f98300,#ffb24d)";

// ─── API ────────────────────────────────────────────────────────────────────
export async function fetchSymbolDetail(symbol) {
  const res = await fetch(`${BASE}/api/symbols/${symbol}/`, { headers: apiHeaders() });
  if (!res.ok) throw new Error(`Symbol not found: ${symbol}`);
  return res.json();
}
export async function fetchChartData(symbol, fromDate, toDate) {
  const res = await fetch(
    `${BASE}/api/chart/${symbol}?from_date=${fromDate}&to_date=${toDate}&is_index=`,
    { headers: apiHeaders() }
  );
  if (!res.ok) throw new Error("Chart data unavailable");
  const data = await res.json();
  return (data.candles || []).map(c => ({
    date: c.timestamp,
    close: parseFloat(c.close_price),
    open: parseFloat(c.open_price),
    high: parseFloat(c.high_price),
    low: parseFloat(c.low_price),
    volume: c.volume,
    ldcp: parseFloat(c.ldcp),
  }));
}
export async function fetchIntradayData(symbol) {
  const res = await fetch(`${BASE}/api/intraday/${symbol}`, { headers: apiHeaders() });
  if (!res.ok) throw new Error("Intraday data unavailable");
  const json = await res.json();
  return (json.data || [])
    .map(([ts, price, volume]) => ({
      date: new Date(ts * 1000).toLocaleTimeString("en-PK", { hour: "2-digit", minute: "2-digit" }),
      close: price, volume, ldcp: null,
    }))
    .reverse();
}
export async function fetchAnnouncements(symbol) {
  const res = await fetch(`${BASE}/api/company/announcements?symbol=${symbol}`, { headers: apiHeaders() });
  if (!res.ok) throw new Error("Announcements unavailable");
  return res.json();
}
export async function fetchFinancials(symbol) {
  const res = await fetch(`${BASE}/api/company/${symbol}/financials/`, { headers: apiHeaders() });
  if (!res.ok) throw new Error("Financials unavailable");
  return res.json();
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
export function formatDate(d) { return d.toISOString().split("T")[0]; }
export function getDateRange(days) {
  const to = new Date(), from = new Date();
  from.setDate(from.getDate() - days);
  return { from: formatDate(from), to: formatDate(to) };
}
export function fmtNum(v, dec = 2) {
  if (v === null || v === undefined) return "—";
  return parseFloat(v).toFixed(dec);
}
export function fmtLarge(v) {
  if (!v) return "—";
  return (v / 1e6).toFixed(2) + " mn";
}

export const RANGES = [
  { label: "1D", days: 0 },
  { label: "1W", days: 7 },
  { label: "1M", days: 30 },
  { label: "3M", days: 90 },
  { label: "6M", days: 180 },
  { label: "1Y", days: 365 },
];

// ─── Icons ──────────────────────────────────────────────────────────────────
const ICON_PATHS = {
  chart: <><polyline points="3 17 9 11 13 15 21 7" /><polyline points="15 7 21 7 21 13" /></>,
  performance: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
  about: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>,
  announcements: <><path d="M3 11l16-5v12L3 14z" /><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" /></>,
  earnings: <><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>,
  eps: <><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></>,
  dividends: <><line x1="19" y1="5" x2="5" y2="19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></>,
  financials: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>,
  ohlcv: <><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="9" y1="3" x2="9" y2="21" /></>,
  pie: <><path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" /></>,
  tech: <><path d="M3.5 18a9 9 0 1 1 17 0" /><line x1="12" y1="15" x2="15.8" y2="10.5" /><circle cx="12" cy="15" r="1.4" fill="currentColor" stroke="none" /></>,
};
export const Icon = ({ name, size = 18, color = "currentColor", strokeWidth = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
       stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    {ICON_PATHS[name] || null}
  </svg>
);

// Section metadata used by sidebar + cards (single source of truth)
export const SECTIONS = [
  { id: "technical",     label: "Technical Analysis", icon: "tech" },
  { id: "chart",         label: "Chart",         icon: "chart" },
  { id: "performance",   label: "Performance",   icon: "performance" },
  { id: "about",         label: "About",         icon: "about" },
  { id: "announcements", label: "Announcements", icon: "announcements" },
  { id: "earnings",      label: "Earnings",      icon: "earnings" },
  { id: "eps",           label: "EPS",           icon: "eps" },
  { id: "dividends",     label: "Dividends",     icon: "dividends" },
  { id: "financials",    label: "Financials",    icon: "financials" },
  { id: "ohlcv",         label: "OHLCV",         icon: "ohlcv" },
];

// ─── Reusable UI ──────────────────────────────────────────────────────────────
export const RangeBtn = ({ label, active, onClick }) => (
  <button onClick={onClick} className={`tdash-rbtn${active ? " active" : ""}`}>{label}</button>
);

export const InfoRow = ({ label, value }) => (
  <div className="tdash-row">
    <span className="tdash-row-label">{label}</span>
    <span className="tdash-row-value">{value ?? "—"}</span>
  </div>
);

export const SectionCard = React.forwardRef(({ id, icon, title, right, children, noPad }, ref) => (
  <section id={id} ref={ref} className="tdash-card">
    <div className="tdash-card-head">
      <div className="tdash-card-title">
        <span className="tdash-card-ic"><Icon name={icon} size={16} /></span>
        <h2>{title}</h2>
      </div>
      {right}
    </div>
    <div className={`tdash-card-body${noPad ? " nopad" : ""}`}>{children}</div>
  </section>
));

export const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: C.panel2, border: `1px solid ${C.border}`,
      borderRadius: 8, padding: "8px 12px", fontSize: 12,
      boxShadow: "0 6px 18px rgba(0,0,0,0.45)", color: C.body,
    }}>
      <div style={{ fontWeight: 700, color: C.ink, marginBottom: 4 }}>{label}</div>
      {payload.map(p => p.value != null && (
        <div key={p.name} style={{ color: p.color, marginBottom: 1 }}>
          {p.name}: <strong>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

export const axisTick = { fontSize: 11, fill: C.faint };
